'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { ExternalLink, Clock, Plus } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { CalendarCheck } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface Appointment {
  id: string;
  customerNumber: string;
  customerName: string;
  scheduledTime: string;
  technician: string;
  paymentMode: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'rescheduled';
  filters: { sedi: boolean; pre: boolean; mem: boolean; post: boolean };
  address: string;
}

const statusBadgeMap: Record<string, 'active' | 'pending' | 'info' | 'inactive' | 'warning'> = {
  scheduled: 'info',
  'in-progress': 'warning',
  completed: 'active',
  rescheduled: 'inactive',
};

export default function AppointmentQueueTable() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [dateLabel, setDateLabel] = useState<string>('');
  const supabase = createClient();

  const fetchTodayAppointments = useCallback(async () => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .eq('scheduled_date', todayStr)
      .neq('status', 'cancelled')
      .order('scheduled_time', { ascending: true });

    if (!error && data) {
      const mapped: Appointment[] = data.map((row) => ({
        id: row.id,
        customerNumber: row.customer_number,
        customerName: row.customer_name,
        scheduledTime: row.scheduled_time?.slice(0, 5) || '',
        technician: row.assigned_technician_name || 'Unassigned',
        paymentMode: row.payment_mode,
        status: row.status as Appointment['status'],
        filters: {
          sedi: row.filter_sedi,
          pre: row.filter_pre,
          mem: row.filter_mem,
          post: row.filter_post,
        },
        address: row.address || '',
      }));
      setAppointments(mapped);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const today = new Date();
    setDateLabel(today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' }));

    fetchTodayAppointments();

    // Real-time: update queue instantly when any appointment changes
    const todayStr = new Date().toISOString().slice(0, 10);
    const channel = supabase
      .channel('appointment-queue-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `scheduled_date=eq.${todayStr}`,
        },
        () => {
          fetchTodayAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchTodayAppointments]);

  const filtered = filter === 'all' ? appointments : appointments.filter((a) => a.status === filter);

  return (
    <div className="bg-card rounded-xl border border-border">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-700 text-foreground">Today&apos;s Appointment Queue</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{dateLabel} — Mon–Fri 8:00 AM – 5:00 PM</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {['all', 'scheduled', 'in-progress', 'completed'].map((s) => (
              <button
                key={`aqf-${s}`}
                onClick={() => setFilter(s)}
                className={`px-2.5 py-1 rounded-md text-xs font-600 transition-colors ${
                  filter === s ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}
              </button>
            ))}
          </div>
          <Link
            href="/scheduling-booking-management"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 btn-primary rounded-lg"
          >
            <Plus size={13} />
            Schedule
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading appointments...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck size={28} />}
          title="No appointments today"
          description="No jobs are scheduled for today. Use the Scheduling module to create and assign appointments to technicians."
          action={
            <Link href="/scheduling-booking-management" className="px-4 py-2 text-sm font-600 btn-primary rounded-lg inline-flex items-center gap-2">
              <Plus size={14} />
              Create First Appointment
            </Link>
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Time</th>
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Technician</th>
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Payment</th>
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Filters</th>
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 text-xs font-600 uppercase tracking-wide text-muted-foreground">Profile</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((appt) => (
                <tr key={appt.id} className="border-b border-border hover:bg-muted/40 transition-colors group">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-600 text-foreground text-sm">{appt.customerName}</p>
                      <p className="text-xs text-muted-foreground font-mono">{appt.customerNumber}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-foreground">
                      <Clock size={13} className="text-muted-foreground" />
                      {appt.scheduledTime}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{appt.technician}</td>
                  <td className="px-4 py-3">
                    <Badge variant="info">{appt.paymentMode}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {['sedi', 'pre', 'mem', 'post'].map((f) => (
                        <span
                          key={`appt-${appt.id}-filter-${f}`}
                          className={`text-xs px-1.5 py-0.5 rounded font-600 ${
                            appt.filters[f as keyof typeof appt.filters]
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusBadgeMap[appt.status] || 'info'}>
                      {appt.status.charAt(0).toUpperCase() + appt.status.slice(1).replace('-', ' ')}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      href="/customer-management"
                      className="flex items-center gap-1 text-xs text-primary font-600 hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ExternalLink size={12} />
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}